import { Component, OnInit, OnDestroy, HostListener, AfterViewInit } from '@angular/core';
import { LastSeenTurbinesService } from './shared/services/last-seen-turbines/last-seen-turbines.service';
import { LanguageService } from './shared/services/language/language.service';
import { firstValueFrom, fromEvent, merge, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import {delay, filter, map, skip, take, takeUntil} from 'rxjs/operators';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import { TrackUserService } from './shared/services/user-track-service/user-track.service';
import { NavigationComponentConfig } from './shared/components/navigation/navigation.component';
import { UserStatsService } from './shared/services/user-stats/user-stats.service';
import moment from 'moment';
import Keycloak from 'keycloak-js';
import { LogoutService } from './shared/services/logout-service/logout.service';
import { LoadNavigationDataService } from './shared/services/data-services/navigation/load-navigation-data.service';
import { ViewChild } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { ReleaseNotesService } from './shared/services-api-central/release-notes/release-notes.service';
import { UserPreferencesService } from './shared/services-api-central/user-preferences/user-preferences.service';
import { TimezoneService } from './shared/services/timezone/timezone.service';
import { loadHighchartsModules, setLangOptions } from './shared/helper/highcharts.helper';
import { DimensionService } from './analyze/service/dimension-service/dimension.service';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { UserPortfolioPreferencesService } from './shared/services/user-preferences/user-portfolio-preferences.service';
import { UserProfileService } from './shared/user-profile/user-profile.service';
import { ConfirmationDialogComponent, IConfirmationDialogOptions } from './shared/components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { PrivacyConsentEnum, UserPreferencesInput } from './shared/notifications-api/graphql';

loadHighchartsModules();

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

	public isSmallerDevice: boolean;
	public theme$: Observable<string>;
	public myNavigationConfig: NavigationComponentConfig;
	private isSmallerDeviceSubscription: Subscription;
	public sidenavDefaultCollapsedState: boolean = false;
	private browserStatusSubscription$: Subscription;
	private unsubscriber = new ReplaySubject();
	public readonly privacyUrl = `https://www.dnv.com/privacy/`;

	@HostListener('window:resize')
  onResize() {
		/* Listener to detect when the sidenav state is collapsed (internally) this is used 
		to prevent the report printing issues on smaller screen sizes (ticket 6760) */
		this.sidenavDefaultCollapsedState = window.innerWidth < 1550;
  }

  @ViewChild('releaseNotesDrawer', { static: true }) releaseNotesDrawer!: MatDrawer;

	constructor(
		private languageService: LanguageService,
		private lastSeenTurbinesService: LastSeenTurbinesService,
		private userPortfolioPreferencesService: UserPortfolioPreferencesService,
		private keycloakService: Keycloak,
		private themeService: ThemeService,
		private trackUserService: TrackUserService,
    	private userProfileService: UserProfileService,
		private userStatsService: UserStatsService,
		private releaseNotesService: ReleaseNotesService,
		private userPreferencesService: UserPreferencesService,
		private logoutService: LogoutService,
		public loadNavigationDataService: LoadNavigationDataService,
    	private translateService: TranslateService,
		private timezoneService: TimezoneService, // Don't delete this line, it's used in the constructor to set the timezone
		private dimensionService: DimensionService,
    	private dialog: MatDialog
	) {
		this.userProfileService.loadUserProfile();
		this.languageService.initLanguage();
		this.theme$ = this.themeService.getTheme();
		this.myNavigationConfig = new NavigationComponentConfig(false);
	}

	async ngOnInit() {

	this.userPreferencesService.getUserPreferencesLoaded()
		.pipe(filter(v => v), takeUntil(this.unsubscriber)).subscribe(() => {
		this.themeListener();
		const settings = this.userPreferencesService.getUserPreferences()
		const hasTimeout = settings?.sessionTimeout ?? false;
		this.userPreferencesService.refreshKeycloakToken(hasTimeout);

		this.theme$.pipe(takeUntil(this.unsubscriber), skip(1)).subscribe((theme: string) => {
			this.userPreferencesService.theme = theme;
		})
		if (settings && settings.privacyConsent == null) {
			this.openPrivacyConsentDialog();
		}		
		})


		this.isSmallerDeviceSubscription = fromEvent(window, 'isSmallerDeviceChanged')
			.pipe(delay(100))
      		.subscribe((event: CustomEvent) => {
        		this.isSmallerDevice = event.detail;
      		});

		await this.sendLoginData();
		

		this.loadUserPreferences();
		this.loadPortfolioPreferences();
		this.startTrackingLastSeenTurbines();
    this.checkBrowserStatus();
		this.subscribeToLanguageChange();
		moment.updateLocale('en', { week: { dow: 1, doy: moment.localeData().firstDayOfYear() } });
	}

	ngAfterViewInit(): void {
		this.releaseNotesService.setDrawer(this.releaseNotesDrawer);
		window.dispatchEvent(new Event('resize'));
	}

	private userLoginRegistration(userId: string) {
		this.userStatsService.login(userId);
	}

	private async sendLoginData(): Promise<any> {
		this.userProfileService.userProfileLoaded().pipe(filter(v => v))
		.pipe(takeUntil(this.unsubscriber))
		.subscribe(() => {
			const userProfile = this.userProfileService.getUserProfile();
			this.userStatsService.login(userProfile?.id);
			const isInternalUser = this.userProfileService.isInternalUser();
			this.userStatsService.loadUser(isInternalUser);
		
				const checkLoginDelayTime = 10 * 1000 /* seconds */
				if (new Date().getTime() - this.keycloakService.idTokenParsed.auth_time * 1000 < checkLoginDelayTime) {
					try {
						this.trackUserService.bindUser();
						this.userLoginRegistration(userProfile?.id);
					} catch (e) {
						console.error(e);
					}
				};
		});
	}


	private loadUserPreferences(): void {
		this.userPortfolioPreferencesService.loadUserPreferences();
	}

	private loadPortfolioPreferences(): void {
		this.userPortfolioPreferencesService.loadPortfolioPreferences();
	}

	private startTrackingLastSeenTurbines(): void {
		this.lastSeenTurbinesService.startTracking();
	}

	private themeListener() {
		const theme = this.userPreferencesService.theme;
		if (!!theme && theme === EThemeName.DARK) {
		this.themeService.setTheme(EThemeName.DARK);
		} else {
		this.themeService.setTheme(EThemeName.LIGHT);
		}
	}

	private subscribeToLanguageChange(): void {
		this.translateService.onLangChange.pipe(
		takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
			setLangOptions(event.lang);
		});
	}

	@HostListener('window:beforeunload')
	unloadNotification() {
		this.userStatsService.trackBeforeUnload();
	}

	ngOnDestroy() {
    if (this.isSmallerDeviceSubscription) {
      this.isSmallerDeviceSubscription.unsubscribe();
    }
    this.browserStatusSubscription$.unsubscribe();
  }

  /* This function detects when the browser is active 
  (after a network disconnection, system suspension, etc...) */
  private checkBrowserStatus() {
    this.browserStatusSubscription$ = merge(
      of(null),
      fromEvent(window, 'online'),
      fromEvent(window, 'offline')
    ).pipe(map(() => navigator.onLine))
    .subscribe(() => {
      if (this.keycloakService.isTokenExpired()) {
        console.warn('JWT Expired! ---- Redirecting to login page ----');
		this.userStatsService.logout();
        this.logoutService.logout()
      }
    });
  }



  private openConfirmModal(options: IConfirmationDialogOptions): MatDialogRef<ConfirmationDialogComponent> {
    return this.dialog.open(ConfirmationDialogComponent, {
      data: options,
      width: options.dialogWidth ? `${options.dialogWidth}px` : '560px',
      maxWidth: '95vw',
      disableClose: options.closable === false,
      autoFocus: false,
    });
  }

  private async openPrivacyConsentDialog(): Promise<void> {
    const dialogRef = this.openConfirmModal({
      message: 'USER_PREFERENCES.PRIVACY.PRIVACY_CONSENT_MODAL',
      messageParams: { privacyUrl: this.privacyUrl },
      title: 'USER_PREFERENCES.PRIVACY.COOKIES_AND_PRIVACY',
      confirmButtonText: 'USER_PREFERENCES.PRIVACY.ACCEPT_ALL',
      cancelButtonText: 'USER_PREFERENCES.PRIVACY.REJECT_ALL',
      closable: false,
      centerButtons: true,
      dialogWidth: 650,
      dialogHeight: 250,
    });

    const result = await firstValueFrom(outputToObservable(dialogRef.componentInstance.confirmed));

    const settings: UserPreferencesInput = {
      privacyConsent: result ? PrivacyConsentEnum.OptedIn : PrivacyConsentEnum.OptedOut,
    };

    this.userPreferencesService.updateUserPreferences(settings).pipe(take(1)).subscribe();

  }



}

